@file:OptIn(ExperimentalResourceApi::class)

package ui.location.detail

import androidx.compose.runtime.*
import androidx.compose.ui.text.input.TextFieldValue
import common.frontend.api.*
import compose.assets.Svg
import compose.assets.svg.*
import io.Repository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.ExperimentalResourceApi
import ssoft.frontend.generated.resources.*
import tk.mallumo.common.msg.Message
import tk.mallumo.compose.navigation.Graph
import tk.mallumo.compose.navigation.LocationContent
import tk.mallumo.compose.navigation.asBundle
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tk.mallumo.log.logERROR
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.TabEntry
import tools.await
import tools.printStackTraceInDebug
import ui.device.DeviceListingArgs
import ui.device.DeviceListingVM
import ui.snackBarStateGlobal

@VM
class LocationVM : NavigationViewModel() {


    private lateinit var scopeUi: LocationScope

    var state by mutableDataState<LocationScope.Wrapper>()
        private set

    val showGroupActions = mutableStateOf(false)

    enum class TabType {
        INFO,
        DEVICES,
        BRANDING,
        BRANDING_PHOTOS,
        EMERGENCY
    }


    var tabsScreenSmall by mutableStateOf(listOf<TabEntry<TabType>>())
        private set

    var tabsScreenLarge by mutableStateOf(listOf<TabEntry<TabType>>())
        private set

    private val _selectedTabSmall = MutableStateFlow<TabEntry<TabType>?>(null)
    val selectedTabSmall: StateFlow<TabEntry<TabType>?> get() = _selectedTabSmall


    private val _selectedTabLarge = MutableStateFlow<TabEntry<TabType>?>(null)
    val selectedTabLarge: StateFlow<TabEntry<TabType>?> get() = _selectedTabLarge

    sealed interface Action {
        data class GroupAction(val act: Message.Task.Keys) : Action
        data class EmergencyActivate(val id: Long) : Action
        data object EmergencyDisable : Action
        data object Save : Action
        data object Delete : Action
        companion object {

            @Composable
            fun rememberAction(vm: LocationVM): (Action) -> Unit {
                val composableContent: MutableState<(@Composable () -> Unit)?> = remember {
                    mutableStateOf(null, policy = neverEqualPolicy())
                }
                composableContent.value?.invoke()

                return remember {
                    { act ->
                        when (act) {
                            Save -> vm.save()
                            Delete -> vm.delete()
                            is GroupAction -> vm.sendGroupAction(act.act)
                            is EmergencyActivate -> vm.emergencyActivate(act.id)
                            EmergencyDisable -> vm.emergencyDisable(composableContent)
                        }
                    }
                }
            }
        }
    }

    private fun emergencyActivate(id: Long) {
        scope.launch {
            state.entry?.also { wrapper ->
                if (!wrapper.canEmergencyVisualActivate) {
                    snackBarStateGlobal.showSnackbar(
                        message = Res.string.location_detail__emergency_activation_fail_permission.await(),
                        withDismissAction = true
                    )
                } else {
                    runCatching {
                        Repository.api.location.emergencyActivate(wrapper.data.id, id)
                    }.onSuccess {
                        wrapper.emergency.activate(id)
                        snackBarStateGlobal.showSnackbar(
                            message = Res.string.location_detail__emergency_activation_success.await(),
                            withDismissAction = true
                        )
                    }.onFailure {
                        it.printStackTrace()
                        snackBarStateGlobal.showSnackbar(
                            message = Res.string.location_detail__emergency_activation_fail_server.await(),
                            withDismissAction = true
                        )
                    }
                }
            }
        }
    }

    private fun emergencyDisable(composableContent: MutableState<@Composable() (() -> Unit)?>) {
        scope.launch {
            state.entry?.also { wrapper ->
                if (!wrapper.canEmergencyVisualActivate) {
                    snackBarStateGlobal.showSnackbar(
                        message = Res.string.location_detail__emergency_disable_fail_permission.await(),
                        withDismissAction = true
                    )
                } else {
                    runCatching {
                        Repository.api.location.emergencyDisable(wrapper.data.id)
                    }.onSuccess {
                        wrapper.emergency.disable()

                        composableContent.value = {
                            scopeUi.nav.findChildNavigation(Graph.LocationContent)
                                ?.viewModel(DeviceListingVM::class, "${wrapper.data.company.id}:${wrapper.data.id}:false")
                                ?.reload()
                            LaunchedEffect(Unit) {
                                composableContent.value = null
                            }
                        }

                        snackBarStateGlobal.showSnackbar(
                            message = Res.string.location_detail__emergency_disable_success.await(),
                            withDismissAction = true
                        )


                    }.onFailure {
                        it.printStackTrace()
                        snackBarStateGlobal.showSnackbar(
                            message = Res.string.location_detail__emergency_disable_fail_server.await(),
                            withDismissAction = true
                        )
                    }
                }
            }
        }
    }

    private fun sendGroupAction(act: Message.Task.Keys) {
        scope.launch {
            runCatching {
                Repository.api.location.groupAction(scopeUi.args.locationId, act)
            }.onFailure {
                logERROR(it)
                snackBarStateGlobal.showSnackbar(
                    message = Res.string.location_detail__group_action__send_fail.await(),
                    withDismissAction = true
                )
            }.onSuccess {
                snackBarStateGlobal.showSnackbar(
                    message = Res.string.location_detail__group_action__send_ok.await(),
                    withDismissAction = true
                )
            }
        }
    }

    fun init(scopeUi: LocationScope) {
        this.scopeUi = scopeUi
        if (state is DataState.Result) return

        scope.launch {
            runCatching {
                val location = Repository.api.location.single(scopeUi.args.locationId)

                configureTabs(scopeUi, location.company.id)

                val wrapper = LocationScope.Wrapper(
                    canEdit = scopeUi.login.canLocationEdit(location.company.id),
                    canDelete = scopeUi.login.canLocationDelete(location.company.id) && location.main == 0,
                    canEmergencyVisualAdd = scopeUi.login.canLocationEmergencyAdd(location.company.id),
                    canEmergencyVisualActivate = scopeUi.login.canLocationEmergencyEdit(location.company.id),
                    data = location
                )
                configureDefaults(wrapper)
                state = DataState.Result(wrapper)
            }.onFailure {
                it.printStackTraceInDebug()
                state = DataState.Error(it.message!!, throwable = it)
            }
        }
    }

    private suspend fun configureDefaults(location: LocationScope.Wrapper) {
        with(location.branding) {
            if (closeWarningTitle.state.isEmpty()) {
                closeWarningTitle.set(TextFieldValue(Res.string.location_detail__branding_close_warn_title.await()))
            }
            if (closeWarningDesc.state.isEmpty()) {
                closeWarningDesc.set(TextFieldValue(Res.string.location_detail__branding_close_warn_desc.await()))
            }
            if (closeWarningAction.state.isEmpty()) {
                closeWarningAction.set(TextFieldValue(Res.string.location_detail__branding_close_warn_action.await()))
            }
        }
    }

    private fun save() {
        scope.launch {
            state.entry
                ?.runCatching {
                    Repository.api.location.edit(data)
                    scopeUi.vmGlobal.reloadCompanyWithLocation(scopeUi.login)
                    this
                }
                ?.onFailure {
                    it.printStackTraceInDebug()
                    snackBarStateGlobal.showSnackbar(
                        it.message!!,
                        withDismissAction = true
                    )
                }
                ?.onSuccess { it.updateOrigin() }
        }
    }

    private fun delete() {
        scope.launch {
            runCatching {
                Repository.api.location.delete(scopeUi.args.locationId)
                scopeUi.vmGlobal.reloadCompanyWithLocation(scopeUi.login)
            }.onSuccess {
                scopeUi.nav.up()
            }.onFailure {
                it.printStackTraceInDebug()
                snackBarStateGlobal.showSnackbar(
                    it.message!!,
                    withDismissAction = true
                )
            }
        }
    }

    private fun configureTabs(scopeUi: LocationScope, companyId: Long) {
        tabsScreenSmall = buildTabs(true, scopeUi, companyId) {
            _selectedTabSmall.value = it
            showGroupActions.value = it.tag == TabType.DEVICES
        }
        tabsScreenLarge = buildTabs(false, scopeUi, companyId) {
            _selectedTabLarge.value = it
            showGroupActions.value = it.tag == TabType.DEVICES
        }
        _selectedTabSmall.value = tabsScreenSmall.first()
        _selectedTabLarge.value = tabsScreenLarge.firstOrNull()

        showGroupActions.value = !scopeUi.display.isMobile && _selectedTabLarge.value?.tag == TabType.DEVICES
    }

    private fun buildTabs(
        withMainScreen: Boolean,
        scopeUi: LocationScope,
        companyId: Long,
        onClick: (TabEntry<TabType>) -> Unit
    ): List<TabEntry<TabType>> {
        return buildList {
            if (withMainScreen) {
                add(
                    TabEntry(
                        icon = Svg.Information_slab_circle_outline,
                        label = Res.string.location_detail__info__title,
                        onClick = onClick,
                        tag = TabType.INFO
                    )
                )
            }
            if (scopeUi.login.canDeviceInfoView(companyId)) {
                add(
                    TabEntry(
                        icon = Svg.Device,
                        label = Res.string.location_detail__device__title,
                        onClick = onClick,
                        args = DeviceListingArgs(
                            companyId = companyId,
                            locationId = scopeUi.args.locationId
                        ).asBundle(),
                        tag = TabType.DEVICES
                    )
                )
            }
            if (scopeUi.login.canLocationEdit(companyId) && scopeUi.login.canLayerBottomEdit(companyId)) {
                add(
                    TabEntry(
                        icon = Svg.Palette,
                        label = Res.string.location_detail__branding__title,
                        onClick = onClick,
                        tag = TabType.BRANDING
                    )
                )
                add(
                    TabEntry(
                        icon = Svg.Image,
                        label = Res.string.location_detail__branding_photo__title,
                        onClick = onClick,
                        tag = TabType.BRANDING_PHOTOS
                    )
                )
            }
            if (scopeUi.login.canLocationEmergencyView(companyId)) {
                add(
                    TabEntry(
                        icon = Svg.Alert_rhombus,
                        label = Res.string.location_detail__emergency,
                        onClick = onClick,
                        tag = TabType.EMERGENCY
                    )
                )
            }
        }
    }

    override fun onRelease() {
        state = DataState.Idle()
    }

}
